import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SessionProvider } from './context/SessionContext.jsx';
import AppShell from './components/AppShell.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import RouteFallback from './components/RouteFallback.jsx';
import Play from './screens/Play.jsx';
import Question from './screens/Question.jsx';

// Play (the landing) + Question (the /q shared-link entry) load EAGERLY so the
// first content paint ships with the initial bundle. The other tabs are
// CODE-SPLIT via React.lazy — they're only reached after the user is already in,
// so deferring their JS (Stats charts, Groups chat/challenges/customization,
// Profile customization, …) shrinks the cold-start download + parse that used to
// be one ~1.2 MB chunk gating Today.
const Results = lazy(() => import('./screens/Results.jsx'));
const Stats = lazy(() => import('./screens/Stats.jsx'));
// STORIES took the Stats tab (SO-4). Stats keeps its route — Profile's
// stats card opens it, and any bookmark or old link still lands somewhere
// real instead of bouncing to Today.
const Stories = lazy(() => import('./screens/Stories.jsx'));
const Groups = lazy(() => import('./screens/Groups.jsx'));
const Profile = lazy(() => import('./screens/Profile.jsx'));
const Leaderboard = lazy(() => import('./screens/Leaderboard.jsx'));
const CoinLedger = lazy(() => import('./screens/CoinLedger.jsx'));
const JoinConfirmation = lazy(() => import('./screens/JoinConfirmation.jsx'));
const ChallengeLanding = lazy(() => import('./screens/ChallengeLanding.jsx'));
const FollowLanding = lazy(() => import('./screens/FollowLanding.jsx'));
const Creators = lazy(() => import('./screens/Creators.jsx'));

// P1 routes — both SPA, served by the `app` Hosting target (every path →
// index.html). The 🔒 [FLIP] surfaces (/i Accept & Play) are NOT here.
//
//   /            → today's six
//   /play        → today's six (canonical)
//   /q/:id       → single shared question, answer-in-place → upsell to /play
// Pull the tab chunks down once the app is quiet (2026-08-11). Code-splitting
// bought a smaller cold start — a real win, kept — but it moved the download
// to the moment of the tap, which is the worst possible time to spend a
// network round trip: the user has just asked for something. Prefetching on
// idle keeps the small first paint AND makes the tabs instant, so the
// fallback above becomes the rare case rather than the normal one.
//
// requestIdleCallback so this never competes with the first paint or the
// day's six; setTimeout is the Safari fallback (no rIC before 17.4). Failures
// are ignored on purpose: a prefetch that misses just means React.lazy does
// the work later, exactly as it does today.
function prefetchTabs() {
  const warm = () => {
    Promise.allSettled([
      import('./screens/Results.jsx'),
      import('./screens/Stories.jsx'),
      import('./screens/Groups.jsx'),
      import('./screens/Profile.jsx'),
    ]);
  };
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(warm, { timeout: 4000 });
  } else {
    window.setTimeout(warm, 2500);
  }
}

export default function App() {
  React.useEffect(() => { prefetchTabs(); }, []);
  return (
    <SessionProvider>
      <AppShell>
        {/* Inside AppShell, around the routes: a screen that throws loses the
            SCREEN, not the tab bar you'd use to leave it. Outside AppShell it
            would blank the nav too, which is the bug it exists to prevent. */}
        <ErrorBoundary>
        {/* The chunk wait now wears the shape of the screen it is fetching
            (RouteFallback) instead of a line of grey text. See that file. */}
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Play />} />
            <Route path="/play" element={<Play />} />
            <Route path="/results" element={<Results />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/creators" element={<Creators />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/coin-ledger" element={<CoinLedger />} />
            <Route path="/q/:questionId" element={<Question />} />
            {/* Web invite/join (the FLIP) — a friend opens play.predictsixapp.com/i/<id>,
                confirms, and joins the inviter's group ON WEB. Founder graduation
                auto-fires server-side. The apex predictsixapp.com/i/<id> stays the
                install funnel; web-shared invites point at this SPA route. */}
            <Route path="/i/:inviteId" element={<JoinConfirmation />} />
            {/* The /q + /s answer-first door (AF-2): ?by=<sharerUid>&q=<qid>.
                The serve pages redirect here; organic links fall through to
                the ballot float inside the screen. */}
            <Route path="/challenge" element={<ChallengeLanding />} />
            {/* Follow / Creator mode (docs/follow-mode-proposal.md) — a user opens
                play.predictsixapp.com/f/<handle>, follows the creator ON WEB, and
                drops into today's six to start comparing. Anonymous-first; no
                chat, no challenge. The apex predictsixapp.com/f/<handle> stays the
                install/link-preview funnel (serveFollowPage). */}
            <Route path="/f/:handle" element={<FollowLanding />} />
            <Route path="*" element={<Navigate to="/play" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </AppShell>
    </SessionProvider>
  );
}
